import React, { Suspense, useEffect } from 'react';
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouteContext,
  useRouter,
} from '@tanstack/react-router';
import { QueryClient } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';

import { Spinfinity, ErrorDisplay } from '@repo/ui';
import { AppErrorBoundary } from '@repo/monitoring';
import { Practitioner } from '@repo/types';

import { getUserInfo, User } from '~/services/auth';
import { isSessionExpiredError } from '@repo/api-client';
import { router } from '~/router';
import AppLayout from '~/components/layouts/AppLayout/AppLayout';
import { practitionerQueryFn } from '~/services/practitioner/usePractitioner';
import { ENVIRONMENT } from '~/config/env';
import useMonitoringIdentity from '~/hooks/useMonitoringIdentity';
import useAmplitudeIdentity from '~/hooks/useAmplitudeIdentity';

import globalCss from '~/assets/global.css?url';
import '~/assets/global.css';
import 'react-toastify/dist/ReactToastify.min.css';
const TanStackRouterDevtools =
  ENVIRONMENT !== 'development'
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import('@tanstack/router-devtools').then(res => ({
          default: res.TanStackRouterDevtools,
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel,
        })),
      );
const ReactQueryDevtools =
  ENVIRONMENT !== 'development'
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import('@tanstack/react-query-devtools').then(res => ({
          default: res.ReactQueryDevtools,
        })),
      );

// No need for custom error boundary - using AppErrorBoundary from @repo/monitoring

const RootComponent = () => {
  useMonitoringIdentity();
  useAmplitudeIdentity();

  return (
    <RootDocument>
      {/* <ReactQueryDevtools /> */}
      {/* <TanStackRouterDevtools /> */}
      <div className='absolute'>
        <ToastContainer
          className='toast-container'
          toastStyle={{
            backgroundColor: 'hsl(72, 26%, 96%)',
            color: 'hsl(255, 7%, 19%)',
          }}
        />
      </div>
      <AppErrorBoundary
        componentName='practitioner-portal'
        fallback={({ error, resetError }) => (
          <ErrorDisplay
            error={error}
            reset={resetError}
            onReload={() => router.invalidate()}
            onLogout={() => {
              window.location.href = '/logout';
            }}
            environment={import.meta.env.VITE_APP_ENVIRONMENT}
            description='Please refresh your screen. If you continue to see an error, reach out to practitioners@tinyhealth.com'
          />
        )}
        sentryTags={{
          app: 'practitioner-portal',
        }}
        ignoreError={isSessionExpiredError}
      >
        <Suspense fallback={<Spinfinity />}>
          <AppLayout>
            <Outlet />
          </AppLayout>
        </Suspense>
      </AppErrorBoundary>
    </RootDocument>
  );
};

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  isAuthenticated: boolean;
  token?: string;
  user: User | null;
  practitioner?: Practitioner | null;
  isPractitioner?: boolean;
  isImpersonating?: boolean;
  impersonationId?: string;
}>()({
  beforeLoad: async ({ context }) => {
    try {
      const { token, user, isImpersonating, impersonationId } =
        await getUserInfo();
      const isPractitioner =
        user?.roles?.some(role =>
          ['practitioner', 'admin', 'impersonate'].includes(role),
        ) ?? false;

      // Don't fetch practitioner data here - let _authed loader handle it
      // This avoids 403 errors due to server context impersonation issues
      const practitioner = null;

      return {
        isAuthenticated: !!token,
        token,
        user,
        isPractitioner,
        practitioner,
        isImpersonating: isImpersonating ?? false,
        impersonationId: impersonationId ?? undefined,
      };
    } catch (error) {
      return {
        isAuthenticated: false,
        token: undefined,
        user: null,
        isPractitioner: false,
        practitioner: null,
        isImpersonating: false,
        impersonationId: undefined,
      };
    }
  },
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'Tiny Health Practitioner Portal',
      },
      {
        name: 'app-version',
        content: 'practitioner-portal-v2',
      },
    ],
    links: [
      {
        rel: 'icon',
        href: '/favicon.svg',
      },
      { rel: 'stylesheet', href: globalCss },
      { rel: 'stylesheet', href: '/_build/assets/style2.css' },
    ],
  }),
  component: RootComponent,
});

const RootDocument = ({ children }: { children: React.ReactNode }) => (
  <>
    <html>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  </>
);
